/**
 * <ParentHeader> — le header de l'espace parent (organism).
 *
 * Quatre choses : la marque (à gauche), de quel enfant on parle (les chips, au centre), l'aide
 * et qui est connecté (la pastille de profil, qui porte la légende « Espace parent »). Pas de roue
 * crantée : le portail Abonnement est devenu un onglet, la roue n'ouvrait que lui. Pas de cœur
 * non plus (Wajih, 28/09) : le parrainage vit dans un bandeau en bas de l'accueil et une ligne du
 * portail Abonnement. À sa place, le **bouton d'aide** : il ouvre la messagerie du support et
 * porte les réponses non lues.
 *
 * Le `<Header>` du DS ne convient pas — il impose un titre centré. Ici le nom de
 * l'espace vit dans la pastille, et le centre est réservé aux chips.
 *
 * **Il ne pose aucune surface, à aucune largeur** : ni fond blanc, ni filet, ni bande. Il flotte
 * sur le fond que peint la coquille — la couleur de page, et la lueur sur l'accueil —
 * et l'écran commence donc d'un seul tenant, comme le livre le dessine (chap. 17, « Le parent,
 * sur ses trois écrans »). Ne reste que la disposition, reprise de la maquette validée :
 *  - **grand écran** : le lockup à gauche, chips au centre, aide + pastille à droite ;
 *  - **téléphone** : macaron + pastille à gauche, aide à droite, puis les chips en bande dessous,
 *    alignées à gauche (elles défilent quand la fratrie déborde).
 *
 * C'est le même composant avec deux dispositions, pas deux headers.
 *
 * **L'espace élève monte ce même header** (son nom neutre attend le câblage) : sa pastille par
 * `profile` (sans légende, sa photo signée, le point d'alerte de son compte), et ce qu'il a gagné dans la
 * fente `center`, là où le parent a ses chips d'enfant. Ni chips ni bouton d'aide : il ne les
 * passe pas.
 *
 * **La marque ramène à l'accueil** quand l'espace le demande (`onPressLogo`) : un toucher, un nom pour
 * le lecteur d'écran, et rien de changé au dessin. Sans lui, la marque n'est qu'une image.
 */
import React from "react"
import { Pressable, useWindowDimensions, View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { StyleSheet } from "react-native-unistyles"

import { type UIParentChild } from "../../../ports/primary/ui/types"
import { Logo } from "../../atoms/Logo"
import { Row } from "../../atoms/containers/Row"
import { breakpoints } from "../../theme/tokens/breakpoints"
import { ChildChips, hasChildChips } from "../../molecules/ChildChips"
import { HelpBubble } from "../../molecules/HelpBubble"
import { ProfilePill, type ProfilePillProps } from "../../molecules/ProfilePill"

export type ParentHeaderProps = {
    /** Prénom du parent, affiché dans la pastille. */
    parentFirstname: string
    /** Image de profil empaquetée dans l'app (import PNG). */
    profileSource?: number
    /** Ouvre la bascule de profil (feuille « Changer de profil »). */
    onSwitchProfile?: () => void
    /** Sous impersonation, la pastille reste décorative (règle du header legacy). */
    profileDisabled?: boolean
    /** Les enfants de la famille — les chips n'apparaissent qu'à partir de deux. */
    childProfiles?: UIParentChild[]
    selectedChildId?: string | number
    /** Enfant vers lequel une bascule est en cours : chips et pastille ne répondent plus jusqu'à la fin. */
    switchingChildId?: string | number | null
    onSelectChild?: (child: UIParentChild) => void
    /**
     * Ouvre la messagerie du support. Absent ⇒ pas de bouton d'aide : un bouton qui n'ouvre rien
     * vaut moins qu'un bouton absent (le messager n'est pas encore prêt, par exemple).
     */
    onContactSupport?: () => void
    /** Réponses du support non lues, sur le bouton d'aide — au-delà de 9, « 9+ ». */
    supportUnreadCount?: number
    /**
     * Ce que la pastille de profil montre d'autre que le prénom. Rien par défaut : la pastille du
     * parent (« Espace parent », l'image de l'app, le chevron).
     */
    profile?: Pick<
        ProfilePillProps,
        "legend" | "avatarUri" | "alert" | "badgeSource" | "chevron" | "signed" | "accessibilityLabel"
    >
    /**
     * Une fente à la place des chips d'enfant : au centre en grand écran, en bande sous la barre
     * sur un téléphone. Rien par défaut.
     */
    center?: React.ReactNode
    /** Toucher la marque ramène à l'accueil de l'espace. Absent : la marque ne presse pas. */
    onPressLogo?: () => void
    testID?: string
}

/**
 * Les chips passent au centre à partir de `md` (800). On mesure la FENÊTRE, pas la colonne de
 * contenu : le header est plein cadre, alors que `DimensionsContext` vaut 2/3 de l'écran sur le
 * web large — il basculerait 400 px trop tard.
 */
const WIDE_FROM = breakpoints.md

/** Hauteurs du logo — le lockup du livre en grand, le macaron seul sur un téléphone. */
const LOGO_HEIGHT = { wide: 56, narrow: 30 }
/** La marque touchable, pour le lecteur d'écran : elle ramène à l'accueil. */
const LOGO_LABEL = "Accueil"

export function ParentHeader(props: ParentHeaderProps) {
    const {
        parentFirstname,
        profileSource,
        onSwitchProfile,
        profileDisabled = false,
        childProfiles = [],
        selectedChildId,
        switchingChildId = null,
        onSelectChild,
        onContactSupport,
        supportUnreadCount = 0,
        profile: profileOverrides,
        center,
        onPressLogo,
        testID,
    } = props

    const { width } = useWindowDimensions()
    const wide = width >= WIDE_FROM
    // Le header est la première surface de l'écran : sur un téléphone il passe sous la barre
    // d'état s'il ne prend pas l'encoche à sa charge (sur le web l'inset vaut zéro).
    const insets = useSafeAreaInsets()
    const showChips = hasChildChips(childProfiles)

    const chips = (
        <ChildChips
            childProfiles={childProfiles}
            selectedChildId={selectedChildId}
            switchingChildId={switchingChildId}
            onSelectChild={onSelectChild}
            // Au centre du header en grand écran ; sur la bande d'un téléphone, à gauche — à
            // quatre enfants la rangée déborde, et centrée elle sortirait des deux côtés.
            center={wide}
        />
    )

    const profile = (
        <ProfilePill
            name={parentFirstname}
            avatarSource={profileSource}
            busy={switchingChildId != null}
            disabled={profileDisabled}
            onPress={onSwitchProfile}
            {...profileOverrides}
        />
    )

    const logo = <Logo variant={wide ? "lockup" : "macaron"} height={wide ? LOGO_HEIGHT.wide : LOGO_HEIGHT.narrow} />

    // À gauche de la pastille en grand écran, au bout de la barre sur un téléphone.
    const help =
        onContactSupport != null ? <HelpBubble onPress={onContactSupport} unreadCount={supportUnreadCount} /> : null

    return (
        <View testID={testID} style={styles.root(insets.top)}>
            <Row align="center" gap="3" style={styles.bar(wide)}>
                <View style={styles.slot}>
                    {onPressLogo != null ? (
                        <Pressable
                            onPress={onPressLogo}
                            accessibilityRole="button"
                            accessibilityLabel={LOGO_LABEL}
                            hitSlop={8}
                        >
                            {logo}
                        </Pressable>
                    ) : (
                        logo
                    )}
                    {/* Sur téléphone la pastille tient compagnie au macaron ; en grand écran elle
                        passe à droite, comme dans la maquette. */}
                    {!wide && profile}
                </View>

                <View style={styles.center}>{wide ? (center ?? (showChips ? chips : null)) : null}</View>

                <Row align="center" gap="2" style={[styles.slot, styles.slotEnd]}>
                    {help}
                    {wide && profile}
                </Row>
            </Row>

            {!wide && center != null && <View style={styles.centerBand}>{center}</View>}
            {!wide && center == null && showChips && <View style={styles.band}>{chips}</View>}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /**
     * Le header ne pose aucune surface, à aucune largeur : ni fond blanc, ni filet. L'écran est
     * d'un seul tenant, et la marque flotte sur le fond que peint la coquille — la couleur de
     * page du thème, et la lueur quand elle porte l'accueil.
     */
    root: (topInset: number) => ({
        paddingTop: topInset,
    }),
    bar: (wide: boolean) => ({
        paddingHorizontal: wide ? theme.spacing["8"] : theme.spacing["3"],
        paddingVertical: wide ? theme.spacing["3"] : theme.spacing["2"],
    }),
    /**
     * Les deux côtés grandissent à parts égales depuis une base nulle : les chips tombent donc au
     * milieu du header, pas au milieu de ce qui reste entre le logo et la pastille.
     */
    slot: {
        flexGrow: 1,
        flexBasis: 0,
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing["2"],
    },
    slotEnd: {
        justifyContent: "flex-end" as const,
    },
    /** `minWidth: 0` autorise la rangée à rétrécir sous la largeur de ses chips, donc à défiler. */
    center: {
        flexShrink: 1,
        minWidth: 0,
    },
    /** Bande des chips en petit écran : posée sur le fond de l'écran, sans filet sous elle. */
    band: {
        paddingVertical: theme.spacing["1"],
    },
    /**
     * La fente en bande : elle s'aligne sur la barre au-dessus d'elle (les chips, elles, portent
     * leur propre marge de défilement).
     */
    centerBand: {
        paddingVertical: theme.spacing["1"],
        paddingHorizontal: theme.spacing["3"],
    },
}))
